import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AbrdnSelect from 'components/Generic/Forms/FormFields/AbrdnSelect';
import Tooltip from 'components/Generic/Tooltip';
import { useI18n } from 'next-localization';
import { useDispatch } from 'react-redux';
import { fundCentreActions } from '@/store/fundCentre';
import { SelectedShareclassType } from 'model/FundCentre';

type Fund = {
  assetClass: string;
  correlationFundRangeId: string;
  id: string;
  name: string;
  sfdrClassification: string;
  selectedShareclass: SelectedShareclassType;
  shareclasses: Array<SelectedShareclassType>;
};

type Props = {
  fund: Fund;
  fundIdx: number;
};

type SelectOption = {
  options: {
    value: any;
  };
  disabled: {
    value: boolean;
  };
};

const FundsShareclassCell: React.FC<Props> = ({ fund, fundIdx }) => {
  const { t } = useI18n();
  const dispatch = useDispatch();
  const [selectOptions, setSelectOptions] = useState<SelectOption>({
    disabled: { value: false },
    options: { value: null },
  });

  const isMoreThanOneOptions = useMemo(() => {
    return fund.shareclasses.length && fund.shareclasses.length >= 2;
  }, [fund.shareclasses]);

  const singleName: string | boolean = useMemo(() => {
    if (!isMoreThanOneOptions) {
      return fund.shareclasses[0]?.shareclassName || t('notAvailableShortMessage');
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMoreThanOneOptions, fund.shareclasses]);

  const buildShareclassSelectorField = useCallback((fund: Fund) => {
    return {
      placeholder: { value: t('noResultsMessage') },
      disabled: { value: !fund.shareclasses.length },
      options: {
        value: fund.shareclasses.map((shareclass) => {
          return {
            name: shareclass.shareclassID,
            label: shareclass.shareclassName,
            value: shareclass.shareclassID,
            selected: shareclass.shareclassID === fund.selectedShareclass?.shareclassID,
          };
        }),
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareclassSelected = useCallback(
    ({ value }: any) => {
      dispatch(
        fundCentreActions.updateSelectedShareclass({ fundIdx: fundIdx, shareclassID: value })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fundIdx]
  );

  useEffect(() => {
    setSelectOptions(buildShareclassSelectorField(fund));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultSelectedOption = fund.shareclasses.find(
    (shareclass) => shareclass.shareclassID === fund.selectedShareclass?.shareclassID
  );

  return (
    <td className="field-text">
      <span className="d-xl-none section-header-small">
        {t('kidShareClass')}
        {t('fundsFundCentreTooltipShareclass') &&
          t('fundsFundCentreTooltipShareclass') !== 'fundsFundCentreTooltipShareclass' && (
            <Tooltip
              arialLabelText={t('kidShareClass')}
              tooltipTitle={t('kidShareClass')}
              tooltipContent={t('fundsFundCentreTooltipShareclass')}
            />
          )}
      </span>
      {singleName ? (
        <p>{singleName}</p>
      ) : (
        <AbrdnSelect
          options={selectOptions.options?.value}
          disabled={selectOptions.disabled?.value}
          defaultValue={
            defaultSelectedOption
              ? {
                  value: defaultSelectedOption.shareclassID,
                  label: defaultSelectedOption.shareclassName,
                }
              : undefined
          }
          className="w-100 shareclass-selector"
          onChange={shareclassSelected}
        />
      )}
    </td>
  );
};

export default FundsShareclassCell;
